/**
 * Conector MCP: las llaves para que un bot externo trabaje con la clínica
 * (2026-09-15; abierto a escribir el 21/9/2026).
 *
 * Un asistente externo entraba abriendo el navegador y pinchando por la pantalla. Con una de
 * estas llaves entra directo. Nace de solo consultar; que además cambie cosas
 * se marca a propósito, y de momento solo escriben las llaves de dirección
 * (backend: mcp_catalogo.ROLES_CON_API_COMPLETA).
 *
 * La llave va atada a una persona, así que cada bot ve exactamente lo que ve
 * esa persona dentro del programa. Se enseña UNA vez al crearla y no se vuelve
 * a poder leer: si se pierde, se revoca y se hace otra.
 */
import React, { useEffect, useState } from 'react';
import { api, getStoredUser, mensajeError } from '../lib/api';
import { SectionCard } from './Common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { isManagement } from '../lib/roles';
import { KeyRound, Copy, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const URL_CONECTOR = `${window.location.origin}/api/mcp`;

function cuando(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

/**
 * Lo que la llave hace DE VERDAD, que no es lo mismo que la casilla (segunda
 * pasada del supervisor, 23/9/2026). Se pintaba «consultar y cambiar» con
 * solo la casilla marcada, también para la llave de una doctora, que recibe
 * las doce consultas de siempre. `escribe` y `entra` los calcula el servidor
 * con la misma regla que aplica el conector (services/mcp_tokens._ficha):
 * aquí no se decide nada, solo se dice.
 */
function queHace(l) {
  if (!l.activo) return l.escritura ? 'tenía marcada la escritura' : 'era de solo consultar';
  if (l.entra === false) return 'no entra: su dueño ya no puede usarla';
  if (l.escribe) return 'consultar y cambiar';
  if (l.escritura) return 'solo consultar (la escritura es solo para dirección)';
  return 'solo consultar';
}

async function copiar(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success('Copiado');
  } catch {
    toast.error('No se ha podido copiar: selecciónalo y cópialo a mano');
  }
}

export default function ConectorMcpPanel() {
  const usuario = getStoredUser();
  const [llaves, setLlaves] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [nombre, setNombre] = useState('');
  // Que la llave además CAMBIE cosas hay que marcarlo a propósito: nace de
  // mirar (21/9/2026, al abrir el conector a todo el producto).
  const [escritura, setEscritura] = useState(false);
  const [deQuien, setDeQuien] = useState('');
  const [creando, setCreando] = useState(false);
  const [reciente, setReciente] = useState(null);   // la única vez que se ve
  // ¿Tiene esta clínica el conector activado? (MCP_ACTIVO en su servidor,
  // 7/10/2026). Mientras no se sepa, no se pinta nada: así el panel no
  // aparece un instante para desaparecer después.
  const [activo, setActivo] = useState(null);

  const puede = isManagement(usuario);

  const cargar = async () => {
    try {
      const r = await api.get('/api/mcp/tokens');
      // Solo `false` lo apaga: una respuesta sin la marca es la de siempre.
      setActivo(r.data?.activo !== false);
      setLlaves(r.data?.tokens || []);
    } catch (e) {
      setActivo(true);
      toast.error(mensajeError(e, 'No se han podido leer las llaves'));
    }
  };

  useEffect(() => {
    if (!puede) return;
    cargar();
    // Los kioscos de tablet no pueden tener llave: el backend los rechaza y
    // aquí ni se ofrecen, para no proponer algo que va a fallar.
    api.get('/api/users').then(r => {
      const activos = (r.data || []).filter(
        u => u.active !== false && !String(u.role || '').startsWith('tablet'));
      setPersonas(activos);
      setDeQuien(d => d || usuario?.id || (activos[0] || {}).id || '');
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puede]);

  // Sin el conector activado no se enseña el panel: la clínica lo pide y
  // ClinicPilot lo enciende en su servidor (no es un ajuste de la pantalla).
  if (!puede || !activo) return null;

  // A nombre de quién se va a crear: para avisar, al marcar la casilla, si
  // esa persona no es de dirección y la escritura no le va a llegar.
  const dueno = personas.find(p => p.id === deQuien);

  const crear = async () => {
    if (!nombre.trim()) { toast.error('Ponle un nombre al conector'); return; }
    setCreando(true);
    try {
      const r = await api.post('/api/mcp/tokens',
        { nombre: nombre.trim(), user_id: deQuien, escritura });
      setReciente(r.data?.token || '');
      setNombre('');
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido crear la llave'));
    } finally { setCreando(false); }
  };

  // Encender o apagar la escritura de una llave que YA está en uso, sin tener
  // que crear otra y reconfigurar el bot (21/9/2026). Sigue siendo a
  // propósito: nadie se encuentra su llave escribiendo sin marcarlo.
  const cambiarEscritura = async (llave) => {
    const dar = !llave.escritura;
    // A la llave de alguien que no es de dirección no se le promete que va a
    // crear y editar: se queda con las consultas de siempre (23/9/2026).
    const pregunta = llave.de_direccion === false
      ? `¿Marcar la escritura en «${llave.nombre}»?\n\n`
        + 'De momento el conector solo deja cambiar cosas a las llaves de dirección: '
        + `la de ${llave.user_nombre || 'su dueño'} se quedará con las consultas de `
        + 'siempre mientras eso no se abra.'
      : `¿Dejar que «${llave.nombre}» cambie cosas en la clínica?\n\n`
        + `Hará lo que haría ${llave.user_nombre || 'su dueño'}: crear y editar citas, `
        + 'pacientes y documentos. Lo que no tiene vuelta atrás se lo seguiremos '
        + 'preguntando.';
    if (dar && !window.confirm(pregunta)) return;
    try {
      const r = await api.put(`/api/mcp/tokens/${llave.id}`, { escritura: dar });
      // Lo que se anuncia es lo que dice el servidor que hace la llave ahora.
      const escribe = !!r?.data?.ficha?.escribe;
      toast.success(!dar ? 'Esa llave vuelve a ser de solo consulta'
        : escribe ? 'Esa llave ya puede trabajar'
          : 'Marcado. Sigue de solo consulta mientras el conector sea solo de dirección');
      await cargar();
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido cambiar')); }
  };

  const revocar = async (llave) => {
    if (!window.confirm(`¿Revocar «${llave.nombre}»? El bot dejará de entrar ahora mismo.`)) return;
    try {
      await api.delete(`/api/mcp/tokens/${llave.id}`);
      toast.success('Llave revocada');
      await cargar();
    } catch (e) { toast.error(mensajeError(e, 'No se ha podido revocar')); }
  };

  return (
    <SectionCard title="Conector para bots" className="lg:col-span-2">
      <p className="text-sm text-muted-foreground mb-4">
        Sirve para que un asistente externo trabaje con la
        clínica sin entrar por la pantalla. Cada llave va a nombre de una persona
        y nunca alcanza <strong>más</strong> de lo que esa persona alcanza dentro
        del programa. De momento, solo las llaves de dirección llegan al programa
        entero; la de cualquier otra persona se queda con las consultas de
        siempre (buscar pacientes y leer su historia, sus citas y sus cirugías).
      </p>
      <p className="text-sm text-muted-foreground mb-4">
        Una llave nace <strong>de solo mirar</strong>. Si además marcas que pueda
        cambiar cosas, y es de dirección, el bot podrá crear, editar y borrar como
        lo haría esa persona; lo que no tiene vuelta atrás (borrar algo, mandar un
        WhatsApp o un correo a una paciente) le exige confirmarlo expresamente.
      </p>
      <p className="text-xs text-muted-foreground mb-5">
        Por esta puerta salen datos de salud a un proveedor externo, y la llave es
        eso: una llave. Quien la tenga hace lo que haría su dueño, así que se da
        como se daría un juego de llaves de la consulta, y se revoca en cuanto
        sobre. Todo lo que hace queda apuntado en Control de rastreo.
      </p>

      <div className="rounded-md border bg-muted/30 p-3 mb-5 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Dirección del conector</div>
            <code className="break-all">{URL_CONECTOR}</code>
          </div>
          <Button variant="outline" size="sm" onClick={() => copiar(URL_CONECTOR)}
            data-testid="mcp-copiar-url"><Copy className="w-4 h-4" /></Button>
        </div>
      </div>

      {reciente && (
        <div className="rounded-md border border-[#2E6E5E] bg-[#2E6E5E]/5 p-4 mb-5"
          data-testid="mcp-token-nuevo">
          <div className="font-semibold text-sm mb-1">Copia la llave ahora</div>
          <p className="text-xs text-muted-foreground mb-2">
            No se vuelve a enseñar. Si se pierde, revócala y haz otra.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <code className="text-xs break-all flex-1">{reciente}</code>
            <Button variant="outline" size="sm" onClick={() => copiar(reciente)}
              data-testid="mcp-copiar-token"><Copy className="w-4 h-4" /></Button>
          </div>
          <div className="text-xs text-muted-foreground">
            En tu asistente, añade un <strong>conector personalizado</strong>: pega la
            dirección del conector y añade la cabecera{' '}
            <code>Authorization: Bearer {'<la llave>'}</code>.
          </div>
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setReciente(null)}
            data-testid="mcp-ocultar-token">Ya la he copiado</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end mb-5">
        <div>
          <Label>Nombre del conector</Label>
          <Input value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Asistente de dirección" data-testid="mcp-nombre" />
        </div>
        <div>
          <Label>A nombre de</Label>
          <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={deQuien} onChange={e => setDeQuien(e.target.value)} data-testid="mcp-usuario">
            {personas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <Button onClick={crear} disabled={creando}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mcp-crear">
          {creando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
          Crear llave
        </Button>
      </div>

      <label className="flex items-start gap-2 text-sm mb-5 cursor-pointer">
        <input type="checkbox" checked={escritura} className="mt-1"
          onChange={e => setEscritura(e.target.checked)} data-testid="mcp-escritura" />
        <span>
          Que además pueda <strong>cambiar cosas</strong> (crear y editar citas,
          pacientes, documentos…), siempre dentro de lo que su dueño puede hacer.
          {escritura && (dueno && !isManagement(dueno) ? (
            // Solo un aviso: quien decide es el servidor, y la lista de abajo
            // pinta lo que la llave hace de verdad (`escribe`).
            <span className="block text-xs text-muted-foreground mt-1"
              data-testid="mcp-aviso-escritura">
              De momento el conector solo deja cambiar cosas a las llaves de
              dirección: la de {dueno.name} se quedará con las consultas de
              siempre aunque lo marques.
            </span>
          ) : (
            <span className="block text-xs text-amber-700 mt-1"
              data-testid="mcp-aviso-escritura">
              Esta llave trabajará en la clínica de verdad. Dásela solo a un bot
              en el que confíes, y revócala en cuanto no haga falta.
            </span>
          ))}
        </span>
      </label>

      {llaves.length === 0 ? (
        <p className="text-sm text-muted-foreground">Todavía no hay ninguna llave.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-2 pr-3">Conector</th>
                <th className="py-2 pr-3">A nombre de</th>
                <th className="py-2 pr-3">Creada</th>
                <th className="py-2 pr-3">Último uso</th>
                <th className="py-2 pr-3 text-right">Consultas</th>
                <th className="py-2 pr-3">Puede</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {llaves.map(l => (
                <tr key={l.id} className={`border-b last:border-0 ${l.activo ? '' : 'text-muted-foreground'}`}
                  data-testid={`mcp-llave-${l.id}`}>
                  <td className="py-2 pr-3">
                    {l.nombre}
                    {!l.activo && <span className="ml-2 text-xs">(revocada)</span>}
                  </td>
                  <td className="py-2 pr-3">{l.user_nombre || '—'}</td>
                  <td className="py-2 pr-3">{cuando(l.creado_at)}</td>
                  <td className="py-2 pr-3">{cuando(l.ultimo_uso_at)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{l.usos}</td>
                  <td className="py-2 pr-3">
                    {l.activo ? (
                      <button onClick={() => cambiarEscritura(l)}
                        data-testid={`mcp-escritura-${l.id}`}
                        className={`text-xs px-2 py-1 rounded border ${l.escribe
                          ? 'border-amber-300 bg-amber-50 text-amber-800'
                          : 'border-border text-muted-foreground'}`}>
                        {queHace(l)}
                      </button>
                    ) : (
                      <span className="text-xs">{queHace(l)}</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {l.activo && (
                      <Button variant="ghost" size="sm" onClick={() => revocar(l)}
                        data-testid={`mcp-revocar-${l.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
